import * as ISchema from "#webapp/src/__schema__";
import TutorialSetMeetingFlexibility from "#webapp/src/components/tutorial-tip/tutorial-set-meeting-flexibility/TutorialSetMeetingFlexibility";
import { useDayOfWeekFlexibilityEligible, useEventFlexibility } from "#webapp/src/hooks";
import { useMonetization } from "#webapp/src/hooks/useMonetization";
import { setCurrentTip } from "#webapp/src/state/actions/week-view.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { TrackingEvents, track } from "#webapp/src/util/analytics.util";
import {
  EFlex,
  SubjectTypeEnum,
  Tags,
  generateEventFormSaveTags,
  getFlexFromEvent,
  getSimilarRoomsOkFromEvent,
  getTagState,
  maybeGetWarningType,
} from "#webapp/src/util/event-tag.util";
import {
  eventHasRoom,
  getBaseExternalEventId,
  getOrganizerNameAndEmailFromAttendeeList,
} from "#webapp/src/util/event.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { useMutation, useQuery } from "@apollo/client";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { eventFromParent, getSetMoveRange } from "@clockwise/client-commons/src/util/event";
import {
  TagState,
  isAutopilotExplicitlyEnabled,
} from "@clockwise/client-commons/src/util/event-tag";
import { convertTimeHoursMinutesToTimeSlot } from "@clockwise/client-commons/src/util/time-slot";
import {
  BatchModifyFlexibilityInput,
  Day,
  DayOfWeek,
  DayOffsetTimeSlot,
  UpdateTimeOfDayTimeSlot,
} from "@clockwise/schema";
import useAddRemoveOrgTags from "@clockwise/web-commons/src/hooks/useAddRemoveOrgTags";
import { SetRoomFlexibilityDocument } from "@clockwise/web-commons/src/mutations/__generated__/SetRoomFlexibility.generated";
import { SetTimeOfDayFlexibilityDocument } from "@clockwise/web-commons/src/mutations/__generated__/SetTimeOfDayFlexibility.generated";
import { UpdateEventFlexibilityDocument } from "@clockwise/web-commons/src/ui/autopilot-flexibility/__generated__/UpdateEventFlexibility.generated";
import { DayOnOffMap } from "@clockwise/web-commons/src/ui/working-hours";
import { isEmpty, isEqual, keys } from "lodash";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { connect, useSelector } from "react-redux";
import { logger } from "../../util/logger.util";
import { AutopilotForm } from "./AutopilotForm";
import { IContainer, IProps, IState } from "./AutopilotFormSaveOnEditTypes";
import { AutopilotFormState } from "./AutopilotFormTypes";
import { AutopilotFormSaveOnEditDocument } from "./apollo/__generated__/AutopilotFormSaveOnEdit.generated";

const ERROR_TOAST_MSG = "Whoops! Please try again";

const dayOnOffMapToDaysOfWeek = (dayOnOffMap: DayOnOffMap) =>
  keys(dayOnOffMap).filter((day) => dayOnOffMap[day as Day]) as Day[];

const getStateFromProps = (props: IProps, currentFlex: EFlex, dayOnOffMap: DayOnOffMap): IState => {
  const { event } = props;
  const flex = currentFlex;

  // if allow similar rooms is null, it hasn't been set yet
  const allowSimilarRooms = getSimilarRoomsOkFromEvent(event, true);
  let suggestedStart: ISchema.IDayOffsetTimeSlot | null = null;
  let suggestedEnd: ISchema.IDayOffsetTimeSlot | null = null;
  let suggestedRoomFlex = false;

  const hasMovementTypeBeenSet = isAutopilotExplicitlyEnabled(event);

  if (!hasMovementTypeBeenSet) {
    // if movement type has never been set, initialize with smart suggestions
    const suggestedAutopilotSettingsResponse = getValue(
      event?.suggestedAutopilotSettingsResponseV3,
      "SuggestedAutopilotSettingsResponseV3",
    );
    suggestedStart =
      suggestedAutopilotSettingsResponse?.suggestedAutopilotSettings?.allowedStartTime ?? null;
    suggestedEnd =
      suggestedAutopilotSettingsResponse?.suggestedAutopilotSettings?.allowedEndTime ?? null;

    suggestedRoomFlex =
      suggestedAutopilotSettingsResponse?.suggestedAutopilotSettings?.isRoomFlexible ?? false;
  }

  const moveRange = getSetMoveRange(event);

  const allowedStart = (moveRange?.allowedStartTime || suggestedStart) ?? null;
  const allowedEnd = (moveRange?.allowedEndTime || suggestedEnd) ?? null;
  const daysOfWeek = dayOnOffMapToDaysOfWeek(dayOnOffMap);

  return {
    allowedStart,
    allowedEnd,
    flex,
    allowSimilarRooms: allowSimilarRooms || suggestedRoomFlex,
    isSaving: false,
    daysOfWeek: daysOfWeek,
  };
};

const getMovementTagStateFromEvent = (event: ISchema.IEvent): TagState | undefined => {
  return getTagState(event, Tags.MovementType, true);
};

const getCalendarIdsFromEvent = (event: ISchema.IEvent) => {
  const calendarIds = [];

  const organizer = event.organizer;

  if (organizer && organizer.urnValue) {
    calendarIds.push(organizer.urnValue);
  }

  return calendarIds;
};

const getUserIdsFromTagState = (tagState?: TagState) => {
  const userIds = [];

  if (tagState && tagState.subjectType === SubjectTypeEnum.User) {
    userIds.push(tagState.subjectValue);
  }

  return userIds;
};

const AutopilotFormSaveOnEditCmpt = (props: IProps) => {
  const {
    event: initialEvent,
    activeViewEvent,
    eventParent: initialEventParent,
    org: initialOrg,
    calendarId,
    attendeeNotInDomain,
    organizerNotClockwiseUser,
    isAllDayEvent: initialIsAllDayEvent,
    enableOnMount,
    setTimeFlexOnMount,
    setRoomFlexOnMount,
    canAutopilot,
    user: initialUser,
    surface,
  } = props;

  const [org, setOrg] = useState(initialOrg);
  const [user, setUser] = useState(initialUser);
  const [event, setEvent] = useState(initialEvent);
  const [eventParent, setEventParent] = useState(initialEventParent);
  const isAllDayEvent = useMemo(() => {
    return initialIsAllDayEvent || event?.isAllDay;
  }, [initialIsAllDayEvent, event?.isAllDay]);
  const [newEventDataHasBeenSet, setNewEventDataHasBeenSet] = useState(false);

  const orgRelayId = org?.id;
  const externalEventId = initialEvent.eventKey.externalEventId;
  const tagState = getMovementTagStateFromEvent(event);

  const calendarIds: string[] = useMemo(() => {
    const newCalendarIds = getCalendarIdsFromEvent(event);
    const isSame = isEqual(calendarIds, newCalendarIds);

    // Prevent unncessary network calls downstream when the calendarIds haven't really changed
    return isSame ? calendarIds : newCalendarIds;
  }, [event]);
  const userIds: string[] = useMemo(() => {
    const newUserIds = getUserIdsFromTagState(tagState);
    const isSame = isEqual(userIds, newUserIds);

    // Prevent unncessary network calls downstrem when the userIds haven't really changed
    return isSame ? userIds : newUserIds;
  }, [tagState]);

  const { data } = useQuery(AutopilotFormSaveOnEditDocument, {
    variables: {
      calendarId,
      typedCalendarId: calendarId,
      orgId: orgRelayId,
      externalEventId,
    },
  });
  const dataViewer = getValue(data?.viewer, "Viewer");
  const dataUser = getValue(dataViewer?.user, "User");
  const dataOrg = getValue(data?.org, "Org");
  const dataEventParent = getValue(dataOrg?.forceFetchEventParentErrorable, "EventParent");
  useEffect(() => {
    if (!dataUser) {
      return;
    }
    setUser(dataUser as ISchema.IUser);
  }, [dataUser]);
  useEffect(() => {
    if (!dataOrg) {
      return;
    }
    setOrg(dataOrg as ISchema.IOrg);
  }, [dataOrg]);
  useEffect(() => {
    if (!dataEventParent) {
      return;
    }
    setEventParent(dataEventParent as ISchema.IEventParent);
    const newEvent = eventFromParent(dataEventParent as ISchema.IEventParent, calendarId);
    setEvent(newEvent as ISchema.IEvent);
    setNewEventDataHasBeenSet(true);
  }, [dataOrg]);

  const dayOfWeekFlexibilityEligible = useDayOfWeekFlexibilityEligible({
    calendarId: calendarId,
    orgId: orgRelayId,
    externalEventId: event.eventKey.externalEventId,
  });

  const {
    eFlex: currentFlex,
    refetchEventFlexibility,
    eventFlexibilityConstraints,
    eventFlexibility,
  } = useEventFlexibility({
    calendarId: calendarId,
    orgId: orgRelayId,
    externalEventId: externalEventId,
  });

  const dayOnOffMap: DayOnOffMap = React.useMemo(() => {
    const nextDayOnOffMap: DayOnOffMap = {};
    if (eventFlexibilityConstraints?.availableMeetingDays) {
      eventFlexibilityConstraints?.availableMeetingDays.forEach(
        (day) => (nextDayOnOffMap[day] = false),
      );
    }
    if (eventFlexibility?.timeRangeFlexibility?.__typename == "DayOfWeekFlexibility") {
      eventFlexibility.timeRangeFlexibility.allowedDays.forEach(
        (day) => (nextDayOnOffMap[day] = true),
      );
    }

    return nextDayOnOffMap;
  }, [eventFlexibilityConstraints?.availableMeetingDays, eventFlexibility?.timeRangeFlexibility]);

  const [updateEventFlexibilityMutation] = useMutation(UpdateEventFlexibilityDocument);

  const updateEventFlexibility = (
    selectedFlex: EFlex,
    selectedDaysOfWeek: Day[],
    onCompleted: () => void,
  ) => {
    let input: BatchModifyFlexibilityInput = {};

    if (selectedFlex === EFlex.Pin) {
      input = {
        orgRelayId: orgRelayId,
        dayOfWeekFlexibleEvents: [],
        weekFlexibleEvents: [],
        dayFlexibleEvents: [],
        notFlexibleEvents: [externalEventId],
      };
    }
    if (selectedFlex === EFlex.Day) {
      input = {
        orgRelayId: orgRelayId,
        dayOfWeekFlexibleEvents: [],
        weekFlexibleEvents: [],
        dayFlexibleEvents: [externalEventId],
        notFlexibleEvents: [],
      };
    }
    if (selectedFlex === EFlex.Week) {
      input = {
        orgRelayId: orgRelayId,
        dayOfWeekFlexibleEvents: [],
        weekFlexibleEvents: [externalEventId],
        dayFlexibleEvents: [],
        notFlexibleEvents: [],
      };
    }
    if (selectedFlex === EFlex.DayOfWeek) {
      const allowedDays = !isEmpty(selectedDaysOfWeek)
        ? ((selectedDaysOfWeek as unknown) as DayOfWeek[])
        : eventFlexibilityConstraints?.availableMeetingDays || [];

      input = {
        orgRelayId: orgRelayId,
        dayOfWeekFlexibleEvents: [
          {
            eventId: externalEventId,
            allowedDays: allowedDays,
          },
        ],
        weekFlexibleEvents: [],
        dayFlexibleEvents: [],
        notFlexibleEvents: [],
      };
    }

    void updateEventFlexibilityMutation({
      variables: {
        input: input,
        CalendarId: calendarId,
      },
      onCompleted: () => {
        void refetchEventFlexibility?.();
        onCompleted();
      },
    });
  };

  const stateFromProps = getStateFromProps(props, currentFlex, dayOnOffMap);

  const [allowedStart, setAllowedStart] = React.useState<
    Omit<DayOffsetTimeSlot, "__typename"> | undefined | null
  >(stateFromProps.allowedStart);

  const [allowedEnd, setAllowedEnd] = React.useState<
    Omit<DayOffsetTimeSlot, "__typename"> | undefined | null
  >(stateFromProps.allowedEnd);

  const start = eventFlexibility?.timeOfDayFlexibility?.start;
  const startTimeSlot = start ? convertTimeHoursMinutesToTimeSlot(start, "start") : null;

  React.useEffect(() => {
    setAllowedStart(startTimeSlot);
  }, [start]);

  const end = eventFlexibility?.timeOfDayFlexibility?.end;
  const endTimeSlot = end ? convertTimeHoursMinutesToTimeSlot(end, "end") : null;

  React.useEffect(() => {
    setAllowedEnd(endTimeSlot);
  }, [end]);

  const [shouldSetTimeOfDayFlex, setShouldSetTimeOfDayFlex] = React.useState(false);
  React.useEffect(() => {
    // Handles reacting to new data from refetches
    if (!shouldSetTimeOfDayFlex) {
      return;
    }
    setAllowedStart(startTimeSlot);
    setAllowedEnd(endTimeSlot);
    setShouldSetTimeOfDayFlex(false);
  }, [shouldSetTimeOfDayFlex]);

  React.useEffect(() => {
    if (!eventFlexibility || eventFlexibility?.isRoomFlexible === null) {
      return;
    }

    setAllowSimilarRooms(eventFlexibility.isRoomFlexible);
  }, [eventFlexibility?.isRoomFlexible]);

  const [flex, setFlex] = React.useState(stateFromProps.flex);
  const [allowSimilarRooms, setAllowSimilarRooms] = React.useState(
    stateFromProps.allowSimilarRooms,
  );
  const [isSaving, setIsSaving] = React.useState(stateFromProps.isSaving);
  const [daysOfWeek, setDaysOfWeek] = React.useState(stateFromProps.daysOfWeek);
  const [saveForm, setSaveForm] = React.useState(false);
  const [callOnSuccess, setCallOnSucess] = React.useState(false);
  const [callOnError, setCallOnError] = React.useState(false);
  const movableUntil = getValue(eventParent?.flexibility)?.movableUntil || null;

  const t = React.useContext(TranslationContext);

  const sendEventAutopilotStatus = (flex: string, allowSimilarRooms: boolean) => {
    const hasRoom = eventHasRoom(event);
    const autopilotEventStatus = getValue(event.autopilotEventStatus);

    const latestDayOnOffMap = dayOnOffMap;
    PostMessageManager.sendEventAutopilotStatus(
      event.eventKey.externalEventId,
      calendarId,
      flex,
      allowSimilarRooms,
      hasRoom,
      !canAutopilot,
      !!event?.annotatedEvent?.orgTags?.find(
        (tag) => tag.tag === "MovementPaused" && tag.state.active,
      ),
      autopilotEventStatus?.status || "",
      attendeeNotInDomain,
      organizerNotClockwiseUser,
      isAllDayEvent,
      latestDayOnOffMap,
      dayOfWeekFlexibilityEligible,
      movableUntil,
      event.isHold,
    );
  };

  const onSaveSuccess = () => {
    setIsSaving(false);

    toast.success(
      flex === EFlex.Pin
        ? (t("autopilot-form:disabled") as string)
        : (t("autopilot-form:updated") as string),
    );
    sendEventAutopilotStatus(flex, !!allowSimilarRooms);
  };

  const onError = () => {
    setIsSaving(false);

    toast.error(ERROR_TOAST_MSG);
    const flex = getFlexFromEvent(event, true);
    const allowSimilarRooms = getSimilarRoomsOkFromEvent(event);

    sendEventAutopilotStatus(flex, !!allowSimilarRooms); // on fail, post message old ap info back so toggles match
  };

  const eventFlexUpdatedMillis = useSelector(
    (state: IReduxState) => state.webExtension.eventFlexUpdatedMillis,
  );

  React.useEffect(() => {
    void refetchEventFlexibility?.();
  }, [eventFlexUpdatedMillis]);

  React.useEffect(() => {
    if (!callOnSuccess) {
      return;
    }

    onSaveSuccess();
    setCallOnSucess(false);
  }, [callOnSuccess, setCallOnSucess]);

  React.useEffect(() => {
    if (!callOnError) {
      return;
    }

    onError();
    setCallOnError(false);
  }, [callOnError, setCallOnError]);

  const [addAndRemoveOrgTags] = useAddRemoveOrgTags();

  // take the form state and update the relevant autopilot tags
  const save = async () => {
    setFlex(flex);
    setIsSaving(true);

    // construct the tags we'll add or remove
    // If event is eligibile for DayOfWeek, we want to use the new mutation
    const allowSimilarRoomsInput = null; // We handle changing `allowSimilarRooms` via GQL mutation, not tags!
    const saveTags = generateEventFormSaveTags(flex, false, false, allowSimilarRoomsInput, true);

    // add the org id and eventparent id to the input
    const input = {
      calendarId,
      orgRelayId: org.id,
      eventParentRelayId: eventParent.id,
      ...saveTags,
    } as ISchema.IAddAndRemoveOrgTagsMutationInput;

    track(TrackingEvents.DEFRAG.COMPLETE_ANNOTATION_SUGGESTION, {
      allowSimilarRooms,
      externalEventId: eventParent.externalEventId,
      flexibility: flex,
    });

    const addAndRemoveOrgTagsWrapped = () => {
      addAndRemoveOrgTags(
        input,
        () => {
          setCallOnSucess(true);
        },
        () => {
          setCallOnError(true);
        },
      );
    };

    // Optimistically update the UI
    setFlex(flex);
    setDaysOfWeek(flex == EFlex.DayOfWeek ? daysOfWeek : []);
    setCallOnSucess(true);
    updateEventFlexibility(flex, daysOfWeek, addAndRemoveOrgTagsWrapped);
  };

  React.useEffect(() => {
    if (!saveForm) {
      return;
    }
    save();

    const baseExternalEventId = getBaseExternalEventId(eventParent.externalEventId);
    track(TrackingEvents.DEFRAG.TOGGLE_AUTOPILOT_SETTING, {
      allowedEnd: formState.timeRange?.allowedEnd,
      allowedStart: formState.timeRange?.allowedStart,
      flex: formState.flex,
      allowSimilarRooms: formState.allowSimilarRooms,
      externalEventId: baseExternalEventId,
    });
    setSaveForm(false);
  }, [saveForm]);

  const [setRoomFlexibilityMutation] = useMutation(SetRoomFlexibilityDocument);
  const [setTimeOfDayFlexibilityMutation] = useMutation(SetTimeOfDayFlexibilityDocument);

  const refetchAndUpdateTimeOfDayUI = () => {
    refetchEventFlexibility?.().finally(() => {
      // Use this flag so can react to the new data
      // and sync the UI to the new data
      setShouldSetTimeOfDayFlex(true);
    });
  };

  const onFormChange = (formState: AutopilotFormState) => {
    const newAllowedStart = formState.timeRange?.allowedStart;
    const newAllowedEnd = formState.timeRange?.allowedEnd;
    const newAllowedStartTimeslot = newAllowedStart?.timeSlot;
    const newAllowedEndTimeslot = newAllowedEnd?.timeSlot;
    const newAllowSimilarRooms = formState.allowSimilarRooms;
    const inputStartTimeslot = newAllowedStartTimeslot || UpdateTimeOfDayTimeSlot.Unbounded;
    const inputEndTimeSlot = newAllowedEndTimeslot || UpdateTimeOfDayTimeSlot.Unbounded;

    // Handles `allowedStart` and `allowedEnd` changes only
    if (
      allowedStart?.timeSlot !== newAllowedStartTimeslot ||
      allowedEnd?.timeSlot !== newAllowedEndTimeslot
    ) {
      // optimistically update UI
      setAllowedStart(newAllowedStart);
      setAllowedEnd(newAllowedEnd);

      void setTimeOfDayFlexibilityMutation({
        variables: {
          calendarId: calendarId,
          input: {
            orgRelayId: org.id,
            updates: [
              {
                allowedStart: inputStartTimeslot as UpdateTimeOfDayTimeSlot,
                allowedEnd: inputEndTimeSlot as UpdateTimeOfDayTimeSlot,
                eventId: externalEventId,
              },
            ],
          },
        },
        onCompleted: (data) => {
          const setTimeOfDayFlexibility = getValue(
            data?.setTimeOfDayFlexibility,
            "TimeOfDayFlexibilityUpdated",
          );
          const updatedEvents = setTimeOfDayFlexibility?.updatedEvents || [];
          const flexibility = getValue(updatedEvents[0]?.flexibility, "EventFlexibility");
          const timeOfDayFlexibility = flexibility?.timeOfDayFlexibility;
          const startTimeSlot = timeOfDayFlexibility?.start
            ? convertTimeHoursMinutesToTimeSlot(timeOfDayFlexibility?.start, "start")
            : null;
          const endTimeSlot = timeOfDayFlexibility?.end
            ? convertTimeHoursMinutesToTimeSlot(timeOfDayFlexibility?.end, "end")
            : null;

          setAllowedStart(startTimeSlot);
          setAllowedEnd(endTimeSlot);

          // Success
          toast.success(t("autopilot-form:updated"));
        },
        onError: () => {
          toast.error(ERROR_TOAST_MSG);
          refetchAndUpdateTimeOfDayUI();
        },
      });

      return;
    }

    if (newAllowSimilarRooms !== allowSimilarRooms) {
      const undoOptimisticUI = () => {
        setAllowSimilarRooms(allowSimilarRooms);
      };

      void setRoomFlexibilityMutation({
        variables: {
          calendarId: calendarId,
          input: {
            orgRelayId: orgRelayId,
            canChangeRooms: newAllowSimilarRooms ? [externalEventId] : [],
            cannotChangeRooms: !newAllowSimilarRooms ? [externalEventId] : [],
          },
        },
        onCompleted: (data) => {
          const setRoomFlexibility = getValue(data.setRoomFlexibility, "RoomFlexibilityUpdated");
          const updatedEvents = setRoomFlexibility?.updatedEvents || [];
          const roomFlexibility = getValue(updatedEvents[0]?.flexibility, "EventFlexibility");
          const isRoomFlexible = roomFlexibility?.isRoomFlexible;

          if (isRoomFlexible === undefined || isRoomFlexible === null) {
            undoOptimisticUI();
            toast.error(ERROR_TOAST_MSG);
          }

          setAllowSimilarRooms(isRoomFlexible);

          // Success
          toast.success(t("autopilot-form:updated") as string);
        },
        onError: (error) => {
          toast.error(ERROR_TOAST_MSG);
          logger.error("An error occured while changing room flexibility", error.message);
        },
      });

      return;
    }

    setFlex(formState.flex);
    setDaysOfWeek(formState.daysOfWeek);
    setSaveForm(true);
  };

  // componentDidMount
  React.useEffect(() => {
    const flex = getFlexFromEvent(event, true);
    let allowSimilarRooms = getSimilarRoomsOkFromEvent(event);
    sendEventAutopilotStatus(flex, !!allowSimilarRooms);

    // enable on mount?
    if (
      ((enableOnMount && flex === EFlex.Pin) || // if ?apenable in url and not toggled on
        (setTimeFlexOnMount === EFlex.Week && flex !== EFlex.Week) || // if ?apflexweek in url and not already set to week
        (setTimeFlexOnMount === EFlex.Day && flex !== EFlex.Day) || // if ?apflexday in url and not already set to day
        (setRoomFlexOnMount && !allowSimilarRooms)) && // if ?apflexroom in url and not already allowing rooms to be enabled
      canAutopilot
    ) {
      setTimeout(() => {
        const baseExternalEventId = getBaseExternalEventId(eventParent.externalEventId);
        track(TrackingEvents.DEFRAG.ENABLE_AUTOPILOT_FROM_URL, {
          externalEventId: baseExternalEventId,
        });

        const suggestedStart =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
              .allowedStartTime) ||
          null;

        const suggestedEnd =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedEndTime) ||
          null;

        const defaultFlex =
          (event.suggestedAutopilotSettingsResponseV3.__typename ===
            "SuggestedAutopilotSettingsResponseV3" &&
            ((event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
              .movementType as unknown) as EFlex)) ||
          EFlex.Day;

        allowSimilarRooms =
          allowSimilarRooms !== null && allowSimilarRooms !== undefined
            ? allowSimilarRooms
            : event.suggestedAutopilotSettingsResponseV3.__typename ===
              "SuggestedAutopilotSettingsResponseV3"
            ? event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.isRoomFlexible
            : false;

        const setMoveRange = getSetMoveRange(event);

        const allowedStart = (setMoveRange?.allowedStartTime || suggestedStart) ?? null;
        const allowedEnd = (setMoveRange?.allowedEndTime || suggestedEnd) ?? null;

        onFormChange({
          timeRange: {
            allowedStart,
            allowedEnd,
          },
          flex: setTimeFlexOnMount ? setTimeFlexOnMount : enableOnMount ? defaultFlex : flex,
          daysOfWeek: [],
          allowSimilarRooms:
            setRoomFlexOnMount !== undefined ? setRoomFlexOnMount : allowSimilarRooms,
        });

        const newPath = window.location.href
          .replace(/[?&]apenable/, "")
          .replace(/[?&]apflexday/, "")
          .replace(/[?&]apflexweek/, "")
          .replace(/[?&]apflexroom/, "");
        window.history.replaceState({ path: newPath }, "", newPath);
      }, 500);
    }
  }, []);

  // componentDidUpdate
  React.useEffect(() => {
    sendEventAutopilotStatus(stateFromProps.flex, !!allowSimilarRooms);
  }, [props, stateFromProps.flex, allowSimilarRooms]);

  // UNSAFE_componentWillReceiveProps
  React.useEffect(() => {
    const newState = getStateFromProps(props, currentFlex, dayOnOffMap);
    setFlex(newState.flex);
    setIsSaving(newState.isSaving);
    setDaysOfWeek(newState.daysOfWeek);
  }, [props, currentFlex, dayOnOffMap, dayOfWeekFlexibilityEligible]);

  const attendeePeople = getValue(event?.attendeePeople);
  const organizerForDisplay = getOrganizerNameAndEmailFromAttendeeList(
    event.organizer?.urnValue,
    attendeePeople,
  );

  const { canUserAccessFlexMeetings } = useMonetization();

  const isOrganizerFreeAndUserPaid = () => {
    const canOrganizerFlexMeeting = getValue(event.featureStatesForOrganizer)?.freePaidFeatures
      ?.autopilotWhenOrganizerCurrentlyAllowed;
    return canUserAccessFlexMeetings && canOrganizerFlexMeeting === false;
  };

  const warning = maybeGetWarningType(
    event,
    canAutopilot,
    attendeeNotInDomain,
    organizerNotClockwiseUser,
    isAllDayEvent,
    isOrganizerFreeAndUserPaid(),
  );

  const hasRoom = eventHasRoom(event);
  const formState = {
    flex: flex,
    daysOfWeek: daysOfWeek,
    allowSimilarRooms: allowSimilarRooms,
    timeRange: {
      allowedStart: allowedStart,
      allowedEnd: allowedEnd,
    },
  } as AutopilotFormState;

  const { trialExpirationStatus } = useMonetization();

  if (!newEventDataHasBeenSet) {
    // We have to wait for the Apollo to get back all data
    // the children Relay fragments require
    return null;
  }

  // When viewing an event that you are not an attendee of.
  // Alternative is to show the toggles but disable them, but the choice is to not show.
  // If canAutoPilot is false, but there is a warning to show, we are currently still showing it. But with
  // the '|| !canAutopilot' below we are correctly disabling the toggle.
  // COULD DO: Could consider not showing the flex controls when canAutopilot is false, but there is a warning to show.
  // I think might be the way to do, I just don't have the full story here and am leaving that decision for another day.
  if (!canAutopilot && !warning) {
    return null;
  }

  return (
    <React.Fragment>
      <TutorialSetMeetingFlexibility userWebAppFlags={user.webAppFlagsErrorable} />
      <AutopilotForm
        org={org}
        event={event}
        calendarId={activeViewEvent.calendarId}
        organizerForDisplay={organizerForDisplay}
        isDisabled={isSaving || !canAutopilot}
        showRoom={hasRoom}
        formState={formState}
        onChange={onFormChange}
        warning={warning}
        style={{ cursor: "pointer" }} // dont show the disabled cursor
        dayOnOffMap={dayOnOffMap}
        showDayOfWeekToggle={dayOfWeekFlexibilityEligible}
        surface={surface}
        trialExpirationStatus={trialExpirationStatus}
        canUserAccessFlexMeetings={canUserAccessFlexMeetings}
      />
    </React.Fragment>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    activeViewEvent: state.webExtension.activeViewEvent,
    mode: state.webExtension.mode,
    contentScrollTop: state.webExtension.contentScrollTop,
    currentTip: state.weekView.currentTip,
  };
}

export const AutopilotFormSaveOnEdit = connect(mapStateToProps, {
  setCurrentTip,
})(AutopilotFormSaveOnEditCmpt);
